import * as THREE from "three";
import { defineModel, type Part, type Vec3 } from "../../lib";

// Dodge Charger (1969, R/T) — the long, wide American muscle coupe at true scale
// (1 unit = 1 metre): length ~5.28, width 1.95, height 1.35, wheelbase 2.97
// (front axle +1.35, rear -1.62), wheel radius ~0.35. Nose is +Z. Authored for
// the `cars` showcase batch. The defining silhouette is a long low body with a
// coke-bottle waist and muscular haunches, and a SEMI-FASTBACK roof with the
// Charger's signature FLYING-BUTTRESS C-pillars: the roof descends into two
// body-colour buttresses that sweep down to a short closed trunk, flanking a
// RECESSED (tunnelled) rear window between them. It is a notchback/tunnel-back,
// never a flat open deck. Signature cues, all present: the FULL-WIDTH BLACK
// GRILLE with HIDDEN HEADLAMPS (a chrome-framed blacked-out grille split by a
// horizontal divider and a centre vertical divider, no visible round lamps); the
// coke-bottle flanks with a round door emblem and a side-scallop hint; the
// full-width black rear tail panel with horizontal tail lamps and twin exhausts;
// a bumblebee black tail band; bright five-spoke Magnum-500 wheels with wide rear
// tyres. Body panels carry the "paint" recolour slot (iconic Dodge Hemi Orange);
// `drive` spins the wheels and `hazards` blinks the six indicators.

const PAINT = "#e2571f"; // Dodge Hemi Orange — the "paint" slot default
const TRIM = "#141518"; // gloss-black trim (grille, tail panel, pillars, band)
const CLAD = "#1c1e22"; // dark valances under the bumpers
const WELL = "#141518"; // wheel-well discs
const GLASS = "#1b2731"; // tinted side / rear glazing — cool, clearly not paint
const SCREEN = "#33485a"; // windscreen (a cooler shade, lighter than the tint)
const CABIN = "#0f1113"; // near-black interior block behind the glass
const CHROME = "#ccd1d6"; // bright chrome — bumpers, grille frame, trim
const SILVER = "#c4c9cf"; // bright Magnum-500 spokes
const DISH = "#101114"; // dark wheel dish behind the spokes
const TIRE = "#1a1b1d";
const BRAKE = "#8c9196";
const GRILLEDK = "#24272e"; // faint grille slat texture on the black grille
const RED = "#b01420"; // tail lamp bar
const REDDARK = "#6e0f18";
const AMBER = "#e8a13a"; // indicators / park lamps
const PLATE = "#eceff2";

// Spec-sheet skeleton (metres). Wide track so the tyres sit at the corners with
// the fat rear tyres proud of the haunches (planted muscle-car stance).
const WHEEL_R = 0.35;
const TYRE_W_F = 0.24;
const TYRE_W_R = 0.30; // wider rear for the muscle-car stance
const WHEEL_X = 0.84; // half track — tyre outer face proud of the body side
const AXLE_F = 1.35;
const AXLE_R = -1.62; // wheelbase 2.97
const BODY_HW = 0.90; // main body-tub half-width (the coke-bottle waist)

const SCREEN_RAKE = 0.60;
const BONNET_TILT = 0.04; // long bonnet, front slightly lower toward the nose

function box(
  w: number, h: number, d: number, color: string,
  position: Vec3, rotation?: Vec3, extra?: Partial<Part>,
): Part {
  return { geometry: new THREE.BoxGeometry(w, h, d), color, position, rotation, ...extra };
}

function cyl(
  rTop: number, rBot: number, h: number, seg: number, color: string,
  position: Vec3, rotation?: Vec3, extra?: Partial<Part>,
): Part {
  return { geometry: new THREE.CylinderGeometry(rTop, rBot, h, seg), color, position, rotation, ...extra };
}

/** A painted body panel box — carries the "paint" recolour slot. */
function paint(w: number, h: number, d: number, position: Vec3, rotation?: Vec3): Part {
  return box(w, h, d, PAINT, position, rotation, { slot: "paint" });
}

/** A painted rounded mass: a unit sphere scaled per axis, never rotated
 *  (rotation applies after scale and would shear a non-uniform blob). Used for
 *  the muscular rear haunches and the softer front fender bulges. */
function blob(scale: Vec3, position: Vec3): Part {
  return { geometry: new THREE.SphereGeometry(1, 16, 12), color: PAINT, position, scale, slot: "paint" };
}

/** One bright five-spoke Magnum-500-style wheel: dark tyre, brake disc, dark
 *  recessed dish, a chrome outer rim ring, five bright flat spokes proud of the
 *  tyre face, chrome centre cap. Every part shares `name` so the `drive` track
 *  spins them as one node about the axle. `side` is +1 for the right (+x) wheel,
 *  -1 for the left. */
function wheel(side: 1 | -1, tyreW: number, z: number, name: string): Part[] {
  const x = side * WHEEL_X;
  const y = WHEEL_R;
  const outer = WHEEL_X + tyreW / 2; // tyre outer face — must stay proud of the well disc
  const parts: Part[] = [
    cyl(WHEEL_R, WHEEL_R, tyreW, 20, TIRE, [x, y, z], [0, 0, Math.PI / 2], { name }),
    cyl(0.15, 0.15, 0.03, 14, BRAKE, [side * (WHEEL_X - 0.05), y, z], [0, 0, Math.PI / 2], { name }),
    cyl(0.245, 0.245, 0.05, 20, DISH, [side * (outer - 0.055), y, z], [0, 0, Math.PI / 2], { name }),
    // chrome outer rim ring, proud of the tyre face
    {
      geometry: new THREE.TorusGeometry(0.30, 0.028, 6, 22),
      color: CHROME,
      position: [side * (outer + 0.006), y, z] as Vec3,
      rotation: [0, Math.PI / 2, 0] as Vec3,
      name,
    },
  ];
  for (let k = 0; k < 5; k++) {
    parts.push(
      box(0.08, 0.52, 0.09, SILVER, [side * (outer - 0.02), y, z], [(k * 2 * Math.PI) / 5, 0, 0], { name }),
    );
  }
  parts.push(cyl(0.07, 0.07, 0.03, 14, CHROME, [side * (outer + 0.004), y, z], [0, 0, Math.PI / 2], { name }));
  return parts;
}

/** The opened wheel well: a dark disc proud of the body side (the arch "cut")
 *  plus a slim body-coloured brow (an upper half-torus over the tyre). The
 *  [0, pi/2, 0] yaw puts the 0..pi arc exactly over the top half; the tyre's
 *  outer face stays proud of both so the wheel is never swallowed. `discX` lets
 *  the rear disc sit slightly outboard under the flared haunch. */
function arch(side: 1 | -1, z: number, discX: number, discR: number, browR: number): Part[] {
  return [
    cyl(discR, discR, 0.04, 20, WELL, [side * discX, WHEEL_R, z], [0, 0, Math.PI / 2]),
    {
      geometry: new THREE.TorusGeometry(browR, 0.05, 6, 20, Math.PI),
      color: PAINT,
      position: [side * (discX + 0.02), WHEEL_R, z] as Vec3,
      rotation: [0, Math.PI / 2, 0] as Vec3,
      slot: "paint",
    },
  ];
}

// The six hazard indicators — parts and blink tracks are generated from one
// table so the pivots (each part's own centre) can never drift from the parts.
// Front pair are amber park lamps at the chrome bumper ends; a mid pair are the
// front amber side-marker chips on the fenders; the rear pair are amber chips at
// the outer ends of the full-width tail lamp bar.
const INDICATORS: ReadonlyArray<{ name: string; pos: Vec3; size: Vec3 }> = [
  { name: "indicator-fl", pos: [-0.72, 0.43, 2.585], size: [0.14, 0.06, 0.03] },
  { name: "indicator-fr", pos: [0.72, 0.43, 2.585], size: [0.14, 0.06, 0.03] },
  { name: "indicator-ml", pos: [-0.905, 0.70, 2.05], size: [0.03, 0.07, 0.14] },
  { name: "indicator-mr", pos: [0.905, 0.70, 2.05], size: [0.03, 0.07, 0.14] },
  { name: "indicator-rl", pos: [-0.80, 0.66, -2.565], size: [0.11, 0.14, 0.05] },
  { name: "indicator-rr", pos: [0.80, 0.66, -2.565], size: [0.11, 0.14, 0.05] },
];

function parts(): Part[] {
  const P: Part[] = [];
  const S = [1, -1] as const;

  // -- Running gear ----------------------------------------------------------
  P.push(
    ...wheel(-1, TYRE_W_F, AXLE_F, "wheel-fl"), ...wheel(1, TYRE_W_F, AXLE_F, "wheel-fr"),
    ...wheel(-1, TYRE_W_R, AXLE_R, "wheel-rl"), ...wheel(1, TYRE_W_R, AXLE_R, "wheel-rr"),
  );
  for (const z of [AXLE_F, AXLE_R]) {
    P.push(cyl(0.05, 0.05, 1.6, 8, TRIM, [0, WHEEL_R, z], [0, 0, Math.PI / 2])); // axle
  }
  for (const s of S) {
    P.push(...arch(s, AXLE_F, 0.90, 0.46, 0.43)); // front arch
    P.push(...arch(s, AXLE_R, 0.93, 0.48, 0.45)); // rear arch, outboard under the haunch
  }
  P.push(box(1.6, 0.14, 4.4, TRIM, [0, 0.28, -0.03])); // low underbody pan, closes the profile

  // -- Body masses (paint): long low tub with a coke-bottle waist -------------
  P.push(paint(BODY_HW * 2, 0.56, 4.5, [0, 0.58, -0.02])); // main tub, sill (0.30) to shoulder (0.86)
  P.push(paint(BODY_HW * 2 + 0.02, 0.06, 3.7, [0, 0.87, -0.05])); // belt-line shoulder band
  // Muscular rear haunches — hip bulges rising over the rear wheels, standing
  // proud of the pinched waist so the flank reads coke-bottle, not slab-sided.
  for (const s of S) P.push(blob([0.16, 0.30, 1.05], [s * 0.80, 0.63, -1.55]));
  // Softer front fender bulges over the front wheels.
  for (const s of S) P.push(blob([0.12, 0.22, 0.66], [s * 0.80, 0.62, 1.30]));

  // -- Long bonnet + fenders (paint) ------------------------------------------
  P.push(paint(1.58, 0.10, 1.78, [0, 0.845, 1.52], [BONNET_TILT, 0, 0])); // long flat bonnet (cowl ~0.63 -> nose)
  for (const s of S) P.push(paint(0.16, 0.12, 1.78, [s * 0.84, 0.845, 1.52], [BONNET_TILT, 0, 0])); // fender crowns
  P.push(paint(1.6, 0.05, 0.14, [0, 0.855, 0.62])); // cowl below the windscreen
  // A single subtle centre power-bulge on the long hood (a '69 cue), body colour.
  P.push(paint(0.5, 0.03, 1.2, [0, 0.9, 1.5], [BONNET_TILT, 0, 0]));

  // -- Nose and tail backing (paint) ------------------------------------------
  P.push(paint(1.8, 0.44, 0.22, [0, 0.64, 2.40])); // nose block (grille sits on it), front ~2.51
  P.push(paint(1.8, 0.44, 0.22, [0, 0.64, -2.40])); // tail block (rear panel sits on it)
  // Fill under the bumpers, back to the wells (no see-through in profile).
  P.push(box(1.5, 0.24, 0.34, CLAD, [0, 0.34, 2.28])); // front valance
  P.push(box(1.5, 0.24, 0.34, CLAD, [0, 0.34, -2.28])); // rear valance

  // -- Greenhouse: a LOW, WIDE cabin — roof nearly body width, well raked, so
  // the greenhouse reads long and low, never a tall box perched on the body.
  P.push(box(1.5, 0.05, 0.14, TRIM, [0, 0.895, 0.72])); // cowl vent at the screen base
  P.push(box(1.56, 0.42, 0.045, SCREEN, [0, 1.08, 0.56], [-SCREEN_RAKE, 0, 0])); // windscreen (wide, raked)
  for (const s of S) P.push(paint(0.10, 0.44, 0.09, [s * 0.72, 1.08, 0.56], [-SCREEN_RAKE, 0, 0])); // A-pillars
  P.push(box(1.52, 0.38, 1.45, CABIN, [0, 1.04, -0.05])); // dark interior block sealing the cabin
  // Short low flat roof, nearly body width; the long descending sweep happens
  // BEHIND it (the buttresses), not on the roof itself.
  P.push(paint(1.66, 0.07, 0.86, [0, 1.28, 0.0])); // roof panel, top ~1.315, rear edge ~ -0.43
  for (const s of S) {
    P.push(box(0.04, 0.36, 0.82, GLASS, [s * 0.78, 1.05, 0.0], [0, 0, s * 0.04])); // side glass (hardtop)
    P.push(box(0.02, 0.03, 0.88, CHROME, [s * 0.795, 0.87, 0.0])); // chrome belt strip under the glass
    // Close the A-pillar/cowl corner so no dark step opens where glass meets body.
    P.push(paint(0.10, 0.14, 0.12, [s * 0.76, 0.9, 0.68]));
  }

  // -- The FLYING-BUTTRESS semi-fastback: a long CLOSED descending sweep --------
  // Two body-colour C-pillar buttresses at the OUTER body edge sweep in one long
  // line from the roof rear ALL THE WAY down toward the tail — this long sweep is
  // the whole rear silhouette, so there is no flat belt-height plane behind the
  // cabin to read as a bed. A recessed, raked rear window is tunnelled BETWEEN
  // the buttresses on a body-colour sill (never see-through), and the sweep lands
  // on a SHORT, LOW ducktail deck. Roof -> buttress -> ducktail is ONE closed
  // mass; there is no open-topped area and no raised lip.
  for (const s of S) {
    P.push(paint(0.46, 0.52, 1.56, [s * 0.67, 0.903, -1.15], [-0.21, 0, 0])); // long flying buttress (outer 0.90)
  }
  P.push(paint(0.84, 0.40, 1.16, [0, 0.80, -1.02], [-0.21, 0, 0])); // body-colour window sill / tunnel floor
  P.push(box(0.80, 0.05, 1.16, GLASS, [0, 1.05, -1.02], [-0.21, 0, 0])); // recessed backlight, ~0.14 below buttress tops
  P.push(paint(0.86, 0.34, 0.5, [0, 0.83, -1.80], [-0.21, 0, 0])); // sail centre closing behind the window (solid)

  // -- Short low ducktail deck the sweep lands on (well below the roofline) -----
  P.push(paint(BODY_HW * 2 - 0.02, 0.30, 0.9, [0, 0.82, -2.10], [-0.06, 0, 0])); // low solid deck, top ~0.98, tail dropping

  // -- Bumblebee tail band (a wide black stripe wrapping the tail) ------------
  P.push(box(BODY_HW * 2 - 0.02, 0.16, 0.34, TRIM, [0, 0.9, -2.28])); // black stripe over the rear deck
  for (const s of S) P.push(box(0.03, 0.16, 0.34, TRIM, [s * (BODY_HW - 0.005), 0.86, -2.28])); // band wrap onto the quarters

  // -- Front fascia: a single full-width BLACK grille blade with HIDDEN lamps ---
  // One blacked-out grille panel in a chrome surround frame with a single
  // central vertical chrome divider (the '69 split). No horizontal chrome slats
  // and no visible round/amber headlamps — the whole front is a wide black blade.
  P.push(box(1.80, 0.40, 0.10, TRIM, [0, 0.66, 2.47])); // full-width blacked-out grille blade (deep)
  for (let i = -8; i <= 8; i++) {
    P.push(box(0.015, 0.34, 0.015, GRILLEDK, [i * 0.098, 0.66, 2.525])); // faint vertical texture only
  }
  P.push(box(1.88, 0.055, 0.08, CHROME, [0, 0.87, 2.52])); // chrome surround — top bar
  P.push(box(1.88, 0.055, 0.08, CHROME, [0, 0.45, 2.52])); // chrome surround — bottom bar
  for (const s of S) P.push(box(0.055, 0.48, 0.08, CHROME, [s * 0.915, 0.66, 2.52])); // chrome surround — end caps
  P.push(box(0.06, 0.40, 0.09, CHROME, [0, 0.66, 2.54])); // single central vertical divider (the '69 split)
  // Chrome front bumper wrapping the corners, below the grille (park lamps ride
  // on its ends — supplied as the front indicators from the table).
  P.push(box(1.88, 0.15, 0.16, CHROME, [0, 0.42, 2.5]));
  for (const s of S) P.push(box(0.15, 0.16, 0.18, CHROME, [s * 0.90, 0.42, 2.4])); // bumper corner wrap
  P.push(box(0.42, 0.12, 0.02, PLATE, [0, 0.42, 2.585])); // front plate on the bumper

  // -- Rear fascia: full-width black tail panel with a horizontal lamp bar -----
  P.push(box(1.8, 0.34, 0.06, TRIM, [0, 0.66, -2.50])); // full-width black tail panel
  // A recessed dark centre panel above the lamps (the "CHARGER" panel hint,
  // no lettering) framed to read as inset.
  P.push(box(1.2, 0.07, 0.03, "#0a0b0d", [0, 0.79, -2.52]));
  // The full-width horizontal tail lamp: red segments split by thin black
  // dividers, proud of the black panel (a long red bar look).
  for (let i = -3; i <= 3; i++) {
    P.push(box(0.19, 0.16, 0.05, RED, [i * 0.225, 0.63, -2.535]));
  }
  for (let i = -3; i < 3; i++) {
    P.push(box(0.02, 0.18, 0.06, TRIM, [i * 0.225 + 0.1125, 0.63, -2.55])); // dividers between the lenses
  }
  P.push(box(1.44, 0.03, 0.05, CHROME, [0, 0.72, -2.54])); // chrome strip above the lamps
  for (const s of S) P.push(box(0.06, 0.04, 0.03, REDDARK, [s * 0.72, 0.5, -2.53])); // low reflectors
  // Chrome rear bumper wrapping the corners.
  P.push(box(1.88, 0.15, 0.16, CHROME, [0, 0.42, -2.5]));
  for (const s of S) P.push(box(0.15, 0.16, 0.18, CHROME, [s * 0.90, 0.42, -2.4])); // bumper corner wrap
  P.push(box(0.42, 0.12, 0.02, PLATE, [0, 0.42, -2.585])); // rear plate on the bumper
  // Twin exhaust tips under the bumper.
  for (const s of S) P.push(cyl(0.05, 0.05, 0.16, 12, CHROME, [s * 0.5, 0.26, -2.5], [Math.PI / 2, 0, 0]));

  // -- Coke-bottle flank details: side crease, door emblem, scallop hint -------
  for (const s of S) {
    P.push(box(0.02, 0.03, 3.2, CHROME, [s * 0.905, 0.68, -0.05])); // full-length body-side chrome crease
    P.push(box(0.03, 0.16, 0.5, "#c1471a", [s * 0.905, 0.7, -0.55])); // subtle door scallop (darker orange)
    P.push(cyl(0.06, 0.06, 0.02, 14, CHROME, [s * 0.912, 0.72, 0.05], [0, 0, Math.PI / 2])); // round door emblem
  }

  // -- Furniture --------------------------------------------------------------
  for (const s of S) {
    P.push(box(0.05, 0.02, 0.03, TRIM, [s * 0.9, 0.9, 0.42])); // mirror arm
    P.push(cyl(0.05, 0.05, 0.02, 12, CHROME, [s * 0.95, 0.91, 0.42], [0, 0, Math.PI / 2])); // round chrome mirror head
    P.push(box(0.018, 0.026, 0.12, CHROME, [s * 0.905, 0.72, -0.45])); // door handle
    P.push(box(0.30, 0.012, 0.03, TRIM, [s * 0.24, 0.86, 0.66], [-SCREEN_RAKE, 0, 0])); // wiper
  }
  for (const ind of INDICATORS) {
    P.push(box(ind.size[0], ind.size[1], ind.size[2], AMBER, ind.pos, undefined, { name: ind.name }));
  }

  return P;
}

// One wheel revolution per cycle; positive x-rotation rolls the car toward +Z.
const WHEEL_PIVOTS: ReadonlyArray<{ name: string; pivot: Vec3 }> = [
  { name: "wheel-fl", pivot: [-WHEEL_X, WHEEL_R, AXLE_F] },
  { name: "wheel-fr", pivot: [WHEEL_X, WHEEL_R, AXLE_F] },
  { name: "wheel-rl", pivot: [-WHEEL_X, WHEEL_R, AXLE_R] },
  { name: "wheel-rr", pivot: [WHEEL_X, WHEEL_R, AXLE_R] },
];

export const dodgeCharger69 = defineModel({
  id: "dodge-charger-69",
  name: "Dodge Charger (1969)",
  batchId: "cars",
  tags: ["car", "dodge", "charger", "muscle", "coupe", "classic"],
  summary: "A 1969 Dodge Charger R/T muscle coupe at true scale, in Hemi Orange.",
  description:
    "A 1969 Dodge Charger R/T at real 1 unit = 1 metre scale (about 5.28 long, " +
    "1.95 wide, 1.35 tall, 2.97 wheelbase, on bright five-spoke Magnum-500 " +
    "wheels with wide rear tyres). The defining silhouette is a long low body " +
    "with a coke-bottle waist and muscular haunches, and a semi-fastback roof " +
    "with the Charger's signature flying-buttress C-pillars sweeping down to a " +
    "short closed trunk, flanking a recessed (tunnelled) rear window. The " +
    "signature cues are all present: the full-width black grille with hidden " +
    "headlamps (a chrome-framed blacked-out grille split by a horizontal and a " +
    "central vertical divider, no visible round lamps); the coke-bottle flanks " +
    "with a round door emblem and a side-scallop hint; the full-width black " +
    "rear tail panel with a horizontal tail lamp bar, twin exhausts and a " +
    "bumblebee tail band; chrome bumpers and side trim. Body panels carry the " +
    "'paint' recolour slot (Hemi Orange); `drive` spins the wheels and " +
    "`hazards` blinks the six indicators.",
  author: "Fable 5",
  footprint: 2.79,
  parts,
  animations: [
    {
      id: "drive",
      label: "Drive",
      duration: 1,
      tracks: WHEEL_PIVOTS.map(({ name, pivot }) => ({
        target: name,
        pivot,
        rotation: [
          { t: 0, value: [0, 0, 0] as Vec3 },
          { t: 0.5, value: [Math.PI, 0, 0] as Vec3 },
          { t: 1, value: [2 * Math.PI, 0, 0] as Vec3 },
        ],
      })),
    },
    {
      id: "hazards",
      label: "Hazard lights",
      duration: 1,
      tracks: INDICATORS.map(({ name, pos }) => ({
        target: name,
        pivot: pos,
        scale: [
          { t: 0, value: 1 },
          { t: 0.42, value: 1 },
          { t: 0.5, value: 0.02 },
          { t: 0.92, value: 0.02 },
          { t: 1, value: 1 },
        ],
      })),
    },
  ],
});
